import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Stethoscope, FlaskConical, HeartPulse, Brain, Activity, ShieldCheck, ArrowRight } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ScrollProgress from "@/components/site/ScrollProgress";
import MobileStickyCTA from "@/components/site/MobileStickyCTA";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { organizationSchema, medicalWebPageSchema, BRAND, OG_IMAGES, SITE_URL} from "@/lib/seo";
import {
  wearableHeroLeft,
  wearableHeroRight,
  sectionTitleFade,
  listItemFade,
} from "./_partials/animationVariants";

const AGENT_URL = process.env.REACT_APP_JOTFORM_INTAKE_URL;

const SERVICES = [
  {
    icon: HeartPulse,
    title: "Hormone Optimization",
    body:
      "Lab-driven testosterone, thyroid, and metabolic balance — built around your unique biology with quarterly titration under physician oversight.",
  },
  {
    icon: FlaskConical,
    title: "Functional Medicine",
    body:
      "Root-cause clinical care — full biomarker mapping, microbiome and inflammation panels, and protocols that address the source, not just the symptom.",
  },
  {
    icon: Brain,
    title: "Longevity Medicine",
    body:
      "Evidence-based longevity protocols built from your DNA, microbiome, biomarkers, and wearables. Bend the curve on biological age.",
  },
  {
    icon: Stethoscope,
    title: "Telemedicine Consultations",
    body:
      "Licensed clinical care delivered nationwide. Your physician, your data, your protocol — without the friction of the traditional clinic visit.",
  },
  {
    icon: Activity,
    title: "Lab-Driven Protocols",
    body:
      "Every recommendation tied to a measurable signal — comprehensive labs, advanced biomarkers, and continuous wearable data fused into one record.",
  },
];

export default function MedicalCare() {
  useDocumentMeta({
    title:
      "Physician-Guided Care | Hormone, Functional & Longevity Medicine | Integrated Wellness",
    description:
      "Licensed physician oversight built into every Integrated Optimization Program — hormone optimization, functional medicine, longevity protocols and telemedicine under Medical Director oversight. Delivered nationwide.",
    image: OG_IMAGES.medicalCare,
    url: `${SITE_URL}/medical-care`,
    schema: [
      organizationSchema(),
      medicalWebPageSchema({
        name: "Precision Medical Care | Integrated Clinical Intelligence",
        about: "Hormone Optimization, Functional Medicine, Longevity Medicine",
      }),
    ],
  });

  return (
    <div data-testid="medical-care-page" className="min-h-screen bg-background">
      <ScrollProgress />
      <Header />
      <main className="pt-28 md:pt-32 pb-24">
        <section className="max-w-6xl mx-auto px-4 md:px-8">
          <Link
            to="/"
            data-testid="back-link"
            className="inline-flex items-center gap-2 font-mono text-[0.65rem] uppercase tracking-[0.32em] text-accent hover:text-primary transition-colors"
          >
            ← Back to Integrated Clinical Intelligence
          </Link>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <motion.div {...wearableHeroLeft} className="lg:col-span-7">
              <div className="iw-overline mb-5">Physician Oversight · Integrated Optimization Programs
              </div>
              <h1 className="font-display font-bold tracking-[-0.04em] leading-[1.0] text-[clamp(2.5rem,5.5vw,4.75rem)]">
                Physician-guided care, <em className="not-italic text-primary">built into every program</em>.
              </h1>
              <p className="mt-6 text-xl md:text-2xl text-foreground/80 leading-relaxed max-w-xl">
                Hormone optimization, functional medicine, longevity protocols, and telemedicine —
                not a separate track, but the clinical backbone of every Integrated Optimization
                Program, delivered nationwide by our licensed clinical team.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a  href={AGENT_URL} data-testid="medical-care-eligibility-cta"
                  className="iw-btn-red inline-flex items-center gap-2 px-7 py-3.5 text-[0.7rem]">
                  Start My Health Assessment <ArrowRight size={16} aria-hidden />
                </a>
                <Link
                  to="/#programs"
                  data-testid="medical-care-to-wellness"
                  className="iw-btn-ghost inline-flex items-center gap-2 px-7 py-3.5 text-[0.7rem]"
                >
                  Explore the programs <span className="text-primary">→</span>
                </Link>
              </div>
            </motion.div>

            <motion.aside {...wearableHeroRight} className="lg:col-span-5">
              <div className="border border-border bg-card p-7 md:p-8">
                <div className="flex items-center gap-2 font-mono text-[0.6rem] uppercase tracking-[0.22em] text-accent mb-3">
                  <ShieldCheck size={14} className="text-accent" /> Under Medical Direction
                </div>
                <h2 className="font-display text-2xl md:text-3xl font-semibold leading-tight">
                  {BRAND.medicalDirector.name}
                </h2>
                <p className="mt-1 text-sm md:text-base text-foreground/55 italic">
                  Medical Director · Internal Medicine · Wellness &amp; Aesthetics
                </p>
                <p className="mt-4 text-base text-foreground/75 leading-relaxed">
                  Dr. Ellis oversees clinical protocols and licensed care across the
                  Integrated Wellness Medical Center platform — ensuring every medical
                  recommendation is grounded in safe, evidence-based practice.
                </p>
                <Link
                  to="/medical-director"
                  data-testid="medical-director-link"
                  className="mt-5 inline-flex items-center gap-1.5 font-display text-base font-semibold text-primary hover:gap-2.5 transition-all"
                >
                  Meet the Medical Director <span aria-hidden>→</span>
                </Link>
              </div>
            </motion.aside>
          </div>
        </section>

        {/* Services */}
        <section className="max-w-6xl mx-auto px-4 md:px-8 mt-24 md:mt-32">
          <motion.div {...sectionTitleFade}>
            <div className="iw-overline mb-4">Clinical Services
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight leading-tight max-w-3xl">
              The clinical care, <em className="not-italic text-primary">unbundled</em>.
            </h2>
          </motion.div>

          <ul className="mt-12 divide-y divide-border border-y border-border">
            {SERVICES.map(({ icon: Icon, title, body }, i) => (
              <motion.li
                key={title}
                {...listItemFade(i)}
                data-testid={`medical-service-${title.toLowerCase().replace(/\s+/g, "-")}`}
                className="grid grid-cols-12 gap-4 md:gap-8 py-8 md:py-10 items-start"
              >
                <div className="col-span-1 pt-1.5">
                  <Icon size={22} className="text-primary" aria-hidden />
                </div>
                <div className="col-span-11 md:col-span-4">
                  <h3 className="font-display text-xl md:text-2xl font-semibold tracking-tight leading-tight">
                    {title}
                  </h3>
                </div>
                <p className="col-span-12 md:col-span-7 text-base text-foreground/75 leading-relaxed">
                  {body}
                </p>
              </motion.li>
            ))}
          </ul>
        </section>

        {/* Closing CTA */}
        <section className="max-w-3xl mx-auto px-4 md:px-8 mt-24 md:mt-32 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight leading-tight">
            See if the Integrated Optimization Program is <em className="not-italic text-primary">right for you</em>.
          </h2>
          <p className="mt-5 text-lg md:text-xl text-foreground/75 leading-relaxed">
            Free eligibility check — your goals, your history, and your access pathway in minutes.
          </p>
          <a  href={AGENT_URL} data-testid="medical-care-closing-cta"
            className="mt-8 iw-btn-red inline-flex items-center gap-2 px-8 py-4 text-[0.75rem]">
            Start My Health Assessment <ArrowRight size={18} aria-hidden />
          </a>
        </section>
      </main>
      <Footer />
      <MobileStickyCTA />
    </div>
  );
}
